// External Imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import ProgressBar from 'react-bootstrap/ProgressBar';

// Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import { useTimezoneContext } from '../utils/TimezoneContext';

import { fetchCustomers, getCustomerHierarchy } from '../utils/customer';
import { fetchStatus } from '../utils/common';
import env from '../utils/env';

const fmtOffset = (mins) => {
  const sign = mins < 0 ? '−' : '+';
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `GMT${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
};

// Every IANA zone, grouped by current GMT offset for <optgroup> headers.
// Intl.supportedValuesOf('timeZone') already returns only canonical zone names (no
// aliases like Asia/Calcutta vs Asia/Kolkata), so no extra dedup is needed here. UTC
// itself isn't part of that list, so it's injected explicitly as its own option —
// it must always be selectable on its own, not merged into whichever city also
// happens to sit at GMT+0. Computed once at module load; sorted ascending, so
// negative offsets sit above UTC and positive offsets (CAT/EAT included) sit below.
const TIMEZONE_GROUPS = (() => {
  const offsetMins = (tz, ts) => {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(ts));
    const v = Object.fromEntries(
      parts.filter((p) => p.type !== 'literal').map((p) => [p.type, +p.value]),
    );
    return (Date.UTC(v.year, v.month - 1, v.day, v.hour % 24, v.minute) - ts) / 60000;
  };
  // Floored to the minute: offsetMins reconstructs a UTC timestamp with seconds
  // zeroed, so comparing it against a `ts` that still has live seconds would leak
  // a fractional minute into every offset.
  const now = Math.floor(Date.now() / 60000) * 60000;
  // UTC listed first so the stable sort below keeps it at the top of the GMT+0
  // group, ahead of whichever city also happens to sit at that offset.
  const zones = [{ value: 'UTC', mins: 0, label: 'UTC' }]
    .concat(
      Intl.supportedValuesOf('timeZone').map((tz) => {
        const mins = offsetMins(tz, now);
        const abbr =
          new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'short' })
            .formatToParts(now)
            .find((p) => p.type === 'timeZoneName')?.value ?? tz;
        const city = (tz.split('/').slice(1).join('/') || tz).replace(/_/g, ' ');
        return { value: tz, mins, label: `${city}  (${abbr})` };
      }),
    )
    .sort((a, b) => a.mins - b.mins);

  const groups = [];
  let lastMins = null;
  zones.forEach((zone) => {
    if (zone.mins !== lastMins) {
      lastMins = zone.mins;
      groups.push({ label: fmtOffset(zone.mins), zones: [] });
    }
    groups[groups.length - 1].zones.push(zone);
  });
  return groups;
})();

const ALL_TIMEZONES = TIMEZONE_GROUPS.flatMap((group) => group.zones);

//Header
export default function Header() {
  const auth = useAuth();
  const { token, name, isValidToken } = useToken();
  const { isLoggedIn, getFullName, getToken, logout } = useBearerToken();
  const { timezone, setTimezone } = useTimezoneContext();

  // Local Data
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState({ variant: 'warning', size: 33, label: 'Loading' });
  const [customers, setCustomers] = useState([]);

  const getStatus = async () => {
    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    fetchStatus(bearerToken)
      .then((data) => {
        if (data['queue']['latest_offset_stored'] == data['data']['offset']) {
          setStatus({
            variant: 'success',
            size: 100,
            label: 'Latest data available.',
          });
        } else {
          setStatus({
            variant: 'danger',
            size: 66,
            label: 'Data is outdated!',
          });
        }
      })
      .catch((error) => {
        setStatus({
          variant: 'danger',
          size: 66,
          label: '' + error,
        });
      });
  };

  const getCustomers = async () => {
    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    fetchCustomers(bearerToken)
      .then((data) => {
        setError(false);
        const customerTree = getCustomerHierarchy(data);
        setCustomers(customerTree);
      })
      .catch((error) => {
        setError(true);
      });

    setLoaded(true);
  };

  useEffect(() => {
    getStatus();
    getCustomers();
  }, [auth, token]);

  // Event Handlers
  const handleLogout = (event) => {
    logout(auth, isValidToken);
  };

  return (
    <Navbar expand='lg' className='bg-white mb-3' variant='light'>
      <Container>
        <Navbar.Brand href='/'>
          <Image
            src={env('REACT_APP_LOGO_URL') || 'https://static.rcswimax.com/images/logo_75px.png'}
            width='50'
            height='50'
          />
          Customer Portal
        </Navbar.Brand>
        <Navbar.Toggle aria-controls='basic-navbar-nav' />
        {isLoggedIn(auth, isValidToken) ? (
          <>
            <Navbar.Collapse id='basic-navbar-nav'>
              <Nav className='me-auto'>
                <NavDropdown title='Customer' id='basic-nav-dropdown'>
                  {loaded ? (
                    <>
                      {error ? (
                        <NavDropdown.Item disabled='true'>
                          Error loading customers!
                        </NavDropdown.Item>
                      ) : (
                        <>
                          {customers.map((customer) => (
                            <NavDropdown.Item
                              key={customer.customer_id}
                              href={'/customer/' + customer.customer_id}
                            >
                              {customer.prefix} {customer.name}
                            </NavDropdown.Item>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <NavDropdown.Item disabled='true'>Loading...</NavDropdown.Item>
                  )}
                </NavDropdown>
                <NavDropdown
                  title={getFullName(auth, isValidToken, name) || name}
                  id='basic-nav-dropdown'
                >
                  <NavDropdown.Item onClick={handleLogout}>Logout</NavDropdown.Item>
                </NavDropdown>
              </Nav>
              <Form.Select
                size='sm'
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                style={{ width: 'auto' }}
                aria-label='Timezone'
              >
                {TIMEZONE_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.zones.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </optgroup>
                ))}
                {!ALL_TIMEZONES.some((z) => z.value === timezone) && (
                  <option value={timezone}>{timezone}</option>
                )}
              </Form.Select>
            </Navbar.Collapse>
            <Navbar.Text className='d-flex'>
              <ProgressBar
                striped
                variant={status['variant']}
                now={status['size']}
                label={status['label']}
                style={{ width: 200 + 'px' }}
              />
            </Navbar.Text>
          </>
        ) : (
          <>
            <Navbar.Collapse id='basic-navbar-nav'>
              <Nav className='me-auto'>
                <Nav.Link href='/staff'>Staff Login</Nav.Link>
                <Nav.Link href='/customer'>Customer Login</Nav.Link>
              </Nav>
            </Navbar.Collapse>
          </>
        )}
      </Container>
    </Navbar>
  );
}
